import { useRequestProgramAddressMutation } from "@/app/api/request-program-address/front";
import { RequestProgramAddressInputSchema } from "@/app/api/request-program-address/schemaTypes";
import { Autocomplete } from "@/components/Autocomplete";
import { Image } from "@/components/Image";
import { PreventScroll } from "@/components/PreventScroll";
import { ArrowRight } from "@/components/icons/ArrowRight";
import { Close } from "@/components/newIcons/Close";
import type { Countries } from "@/queries/getCountries";
import { valibotResolver } from "@hookform/resolvers/valibot";
import Link from "next/link";
import { useState } from "react";
import usePortal from "react-cool-portal";
import { type SubmitHandler, useForm } from "react-hook-form";
import { Dropdown, Input } from "ui";
import { PrimaryButton } from "ui/components";
import type { InferInput, InferOutput } from "valibot";

type Props = {
  email: string;
  telephone: string | null;
  brochureOperationId: number;
  onSuccess?: () => void;
  onHide: () => void;
  countries: Countries;
};
export const AddressForm = ({ email, telephone, brochureOperationId, onSuccess, onHide, countries }: Props) => {
  const [showFullAddressForm, setShowFullAddressForm] = useState(false);
  const { Portal } = usePortal({
    defaultShow: true,
    internalShowHide: false,
  });
  const { control, handleSubmit, setValue } = useForm<
    InferInput<RequestProgramAddressInputSchema>,
    unknown,
    InferOutput<RequestProgramAddressInputSchema>
  >({
    resolver: valibotResolver(RequestProgramAddressInputSchema),
    defaultValues: {
      title: "Mme",
      countryId: "FR",
      firstname: "",
      lastname: "",
      email,
      address1: "",
      address2: "",
      zip: "",
      city: "",
      telephone,
      birthdate: "",
      brochureOperationId,
    } satisfies InferInput<RequestProgramAddressInputSchema>,
  });
  const [request, { isLoading }] = useRequestProgramAddressMutation({
    onCompleted: (data) => {
      if (data.success) {
        onSuccess?.();
      }
    },
  });

  const onSubmit: SubmitHandler<InferOutput<RequestProgramAddressInputSchema>> = (data) => {
    request(data);
  };

  return (
    <Portal>
      <div onClick={onHide} className="fixed inset-0 z-50 bg-primary/80 p-4 xl:pt-10">
        <PreventScroll />
        <form
          className="relative w-full max-w-2xl mx-auto bg-white p-4 lg:p-6 border border-solid border-[#E5E5E5] rounded-[5px] shadow-sm text-primary flex flex-col space-y-4 overflow-y-auto max-h-[calc(100vh-32px)] xl:max-h-[calc(100vh-80px)]"
          onClick={(e) => e.stopPropagation()}
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="grid md:grid-cols-[minmax(0,1fr)_160px] gap-4 grow">
            <div className="flex items-center space-x-4">
              <p className="text-lg lg:text-xl leading-6 font-semibold">
                Renseignez votre adresse postale et recevez gratuitement notre dernier catalogue
              </p>
              <button
                type="button"
                className="border-2 border-[#C1B1AC] bg-white rounded-lg p-2 md:absolute md:top-1 md:right-1"
                onClick={onHide}
              >
                <Close className="w-4 h-4 text-primary" />
              </button>
            </div>
            <div className="md:row-span-2 max-md:w-32 max-md:mx-auto max-md:order-last p-0 md:p-6 lg:p-5">
              <Image
                image={{
                  type: "asset",
                  name: "brochure_2027_1.png",
                  realname: "brochure_2027_1.png",
                }}
                alt="Catalogue"
                objectFit="cover"
                sizes="240px"
                height={{ type: "ratio", value: 133.75 }}
              />
            </div>
            <p className="flex items-end">Rappelez-vous : tout reste entre nous !</p>
          </div>
          <div className="grid grid-cols-[90px_minmax(0,1fr)_minmax(0,1fr)] gap-4">
            <Dropdown
              id="civilite"
              label="Civilité *"
              name="title"
              control={control}
              items={[
                { text: "Mme", callbackValue: "Mme" },
                { text: "M.", callbackValue: "M." },
              ]}
            />
            <Input id="firstname" label="Prénom *" name="firstname" control={control} />
            <Input id="lastname" label="Nom *" name="lastname" control={control} />
          </div>
          <Input
            id="birthdate"
            label="Date de naissance*"
            name="birthdate"
            control={control}
            pattern="\d{2}-\d{2}-\d{4}"
            placeholder="--/--/----"
            type="date"
            inputClassName="no-date-icon"
          />
          {!showFullAddressForm && (
            <div className="flex flex-col space-y-2">
              <Autocomplete
                onSelect={(result) => {
                  setValue("address1", result.addressLine1 || "");
                  setValue("zip", result.postalCode || "");
                  setValue("city", result.city || "");
                  setValue("countryId", result.country || "");
                  setShowFullAddressForm(true);
                }}
              />
              <p
                onClick={() => setShowFullAddressForm(true)}
                className="underline text-xs cursor-pointer text-[#4c4c4c]"
              >
                Saisir mon adresse manuellement
              </p>
            </div>
          )}
          {showFullAddressForm && (
            <>
              <Input id="address-line1" label="Adresse ligne 1*" name="address1" control={control} />
              <Input id="address-line2" label="Adresse ligne 2" name="address2" control={control} />
              <div className="grid grid-cols-3 gap-4">
                <Input id="postal-code" label="Code postal*" name="zip" control={control} />
                <Input id="address-level2" label="Ville*" name="city" control={control} />
                <Dropdown
                  id="country"
                  label="Pays *"
                  name="countryId"
                  control={control}
                  items={countries.map((c) => ({
                    text: c.label,
                    callbackValue: c.id,
                  }))}
                />
              </div>
            </>
          )}
          <div className="flex justify-end">
            <PrimaryButton type="submit" disabled={isLoading} className="w-full lg:w-auto">
              Recevoir le catalogue
              <ArrowRight className="w-4 h-4" />
            </PrimaryButton>
          </div>
          <p className="text-xs text-tx-1">
            * En demandant à recevoir notre catalogue de voyages, vous acceptez également de recevoir par courrier nos
            prochaines éditions. Vous pouvez à tout moment vous opposer à leur envoi en nous contactant à l’adresse
            e-mail :{" "}
            <Link href="mailto:info@intermedes.com" className="text-tx-1 underline">
              info@intermedes.com
            </Link>
            .
          </p>
        </form>
      </div>
    </Portal>
  );
};
